import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createTopicArea, getTopicArea, getTopicAreas, updateTopicArea } from '../api/topicAreaApi';
import axiosInstance from '../api/axiosInstance';
import CategoryAutocomplete from '../components/UI/CategoryAutocomplete';
import { TopicArea } from '../types';
import './TopicAreaDetailPage.css';

const collectCategoryIds = (area: Partial<TopicArea>): number[] =>
  [area.main_category1_id, area.main_category2_id, area.main_category3_id].filter(
    (id): id is number => typeof id === 'number'
  );

const normalizeTags = (values: string[]): string[] =>
  values
    .flatMap(value => value.split(','))
    .map(value => value.trim())
    .filter(Boolean)
    .filter((value, index, array) => array.indexOf(value) === index);

const TopicAreaFormPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [startYear, setStartYear] = useState('');
  const [startMonth, setStartMonth] = useState('');
  const [endYear, setEndYear] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [isCurrent, setIsCurrent] = useState(false);
  const [timelineDisplayCompact, setTimelineDisplayCompact] = useState(true);
  const [isPublic, setIsPublic] = useState(true);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [userCategoryIds, setUserCategoryIds] = useState<number[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    const loadMyCategories = async () => {
      try {
        const myAreas = await getTopicAreas(true);
        const categoryIds = myAreas.flatMap(collectCategoryIds);
        setUserCategoryIds(Array.from(new Set(categoryIds)));
      } catch (error) {
        console.error('내 카테고리 이력 로딩 실패:', error);
      }
    };

    loadMyCategories();
  }, []);

  useEffect(() => {
    if (!isEdit || !id) return;

    setLoading(true);
    getTopicArea(Number(id))
      .then(area => {
        setTitle(area.title || '');
        setSummary(area.summary || '');
        setContent(area.content || '');
        setStartYear(area.start_year ? String(area.start_year) : '');
        setStartMonth(area.start_month ? String(area.start_month) : '');
        setEndYear(area.end_year ? String(area.end_year) : '');
        setEndMonth(area.end_month ? String(area.end_month) : '');
        setIsCurrent(area.is_current === 'Y');
        setTimelineDisplayCompact(area.timeline_display_compact !== 'N');
        setIsPublic(area.is_public !== 'N');
        setSelectedCategoryIds(collectCategoryIds(area));

        axiosInstance.get(`/api/topic-area-tags/${id}`).then(response => {
          setTags(response.data.map((tag: any) => tag.name));
        });
      })
      .finally(() => setLoading(false));
  }, [isEdit, id]);

  const yearOptions = useMemo(() => Array.from({ length: 171 }, (_, index) => 1890 + index), []);
  const monthOptions = useMemo(() => Array.from({ length: 12 }, (_, index) => index + 1), []);

  const handleAddTag = () => {
    const nextTags = normalizeTags([...tags, tagInput]);
    if (nextTags.length === tags.length) return;
    setTags(nextTags);
    setTagInput('');
  };

  const handleTagInputChange = (value: string) => {
    if (!value.includes(',')) {
      setTagInput(value);
      return;
    }

    const nextTags = normalizeTags([...tags, value]);
    setTags(nextTags);
    setTagInput('');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!title.trim()) {
      alert('제목을 입력하세요.');
      return;
    }

    setLoading(true);
    try {
      const normalizedTags = normalizeTags([...tags, tagInput]);
      const payload: any = {
        title: title.trim(),
        summary: summary.trim(),
        content: content.trim(),
        start_year: startYear ? parseInt(startYear, 10) : null,
        start_month: startMonth ? parseInt(startMonth, 10) : null,
        end_year: isCurrent ? null : endYear ? parseInt(endYear, 10) : null,
        end_month: isCurrent ? null : endMonth ? parseInt(endMonth, 10) : null,
        is_current: isCurrent ? 'Y' : 'N',
        timeline_display_compact: timelineDisplayCompact ? 'Y' : 'N',
        is_public: isPublic ? 'Y' : 'N',
        main_category1_id: selectedCategoryIds[0] || null,
        main_category2_id: selectedCategoryIds[1] || null,
        main_category3_id: selectedCategoryIds[2] || null,
        tags: normalizedTags
      };

      let topicAreaId: number | string;
      if (isEdit && id) {
        await updateTopicArea(Number(id), payload);
        topicAreaId = id;
        alert('수정되었습니다.');
      } else {
        const response = await createTopicArea(payload);
        topicAreaId = response.id;
        alert('등록되었습니다.');
      }

      await axiosInstance.post('/api/topic-area-tags/link', {
        topic_area_id: topicAreaId,
        tag_names: normalizedTags
      });

      navigate(`/topic-areas/${topicAreaId}`);
    } catch (error) {
      console.error('주제영역 저장 실패:', error);
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="topic-detail-container">
      <h1 style={{ fontSize: 26, marginBottom: 24, borderBottom: '1px solid var(--border)', paddingBottom: 16 }}>
        {isEdit ? '주제영역 수정' : '주제영역 등록'}
      </h1>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">제목</label>
          <input
            type="text"
            value={title}
            onChange={event => setTitle(event.target.value)}
            placeholder="주제영역의 제목을 입력하세요"
            className="modern-input"
          />
        </div>

        <div className="form-group">
          <label className="form-label">요약 설명</label>
          <textarea
            value={summary}
            onChange={event => setSummary(event.target.value)}
            placeholder="목록에 표시할 간단한 설명을 입력하세요"
            className="modern-textarea"
            rows={2}
          />
        </div>

        <div className="form-group">
          <label className="form-label">상세 내용</label>
          <textarea
            value={content}
            onChange={event => setContent(event.target.value)}
            placeholder="주제영역의 배경과 범위를 입력하세요"
            className="modern-textarea"
            rows={6}
          />
        </div>

        <div className="form-group" style={{ backgroundColor: 'var(--background)', padding: 20, borderRadius: 8, marginBottom: 20 }}>
          <label className="form-label" style={{ marginBottom: 12 }}>아카이브 대상 기간 범위</label>

          <div className="form-row">
            <div className="form-row-col">
              <label className="form-label">시작 연도</label>
              <select value={startYear} onChange={event => setStartYear(event.target.value)} className="modern-select">
                <option value="">연도 선택</option>
                {yearOptions.map(year => (
                  <option key={year} value={year}>{year}년</option>
                ))}
              </select>
            </div>

            <div className="form-row-col">
              <label className="form-label">시작 월</label>
              <select value={startMonth} onChange={event => setStartMonth(event.target.value)} className="modern-select">
                <option value="">월 선택</option>
                {monthOptions.map(month => (
                  <option key={month} value={month}>{month}월</option>
                ))}
              </select>
            </div>

            {!isCurrent && (
              <>
                <div className="form-row-col">
                  <label className="form-label">종료 연도</label>
                  <select value={endYear} onChange={event => setEndYear(event.target.value)} className="modern-select">
                    <option value="">연도 선택</option>
                    {yearOptions.map(year => (
                      <option key={year} value={year}>{year}년</option>
                    ))}
                  </select>
                </div>

                <div className="form-row-col">
                  <label className="form-label">종료 월</label>
                  <select value={endMonth} onChange={event => setEndMonth(event.target.value)} className="modern-select">
                    <option value="">월 선택</option>
                    {monthOptions.map(month => (
                      <option key={month} value={month}>{month}월</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            <div className="form-row-col" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 6 }}>
              <label className="modern-checkbox-container" htmlFor="isCurrent">
                <input
                  type="checkbox"
                  id="isCurrent"
                  checked={isCurrent}
                  onChange={event => setIsCurrent(event.target.checked)}
                  className="modern-checkbox"
                />
                <span>현재 진행 중인 주제</span>
              </label>
            </div>

            <div className="form-row-col" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 6 }}>
              <label className="modern-checkbox-container" htmlFor="timelineDisplayCompact">
                <input
                  type="checkbox"
                  id="timelineDisplayCompact"
                  checked={timelineDisplayCompact}
                  onChange={event => setTimelineDisplayCompact(event.target.checked)}
                  className="modern-checkbox"
                />
                <span>간략하게 표시</span>
              </label>
            </div>
          </div>
        </div>

        <div className="form-group" style={{ backgroundColor: 'var(--background)', padding: '16px 20px', borderRadius: 8, marginBottom: 20 }}>
          <label className="modern-checkbox-container" htmlFor="isPublic">
            <input
              type="checkbox"
              id="isPublic"
              checked={isPublic}
              onChange={event => setIsPublic(event.target.checked)}
              className="modern-checkbox"
            />
            <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>공개 설정</span>
          </label>
        </div>

        <div className="form-group" style={{ backgroundColor: 'var(--background)', padding: 20, borderRadius: 8, marginBottom: 20 }}>
          <label className="form-label" style={{ display: 'block', marginBottom: 12 }}>주요 카테고리 분류 (최대 3개)</label>
          <CategoryAutocomplete
            selectedIds={selectedCategoryIds}
            onChange={setSelectedCategoryIds}
            maxSelections={3}
            userCategoryIds={userCategoryIds}
          />
        </div>

        <div className="form-group">
          <label className="form-label">키워드 태그 입력</label>
          <input
            type="text"
            value={tagInput}
            onChange={event => handleTagInputChange(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ',') {
                event.preventDefault();
                handleAddTag();
              }
            }}
            placeholder="태그를 입력하고 Enter 또는 쉼표를 누르세요"
            className="modern-input"
          />

          {tags.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              {tags.map(tag => (
                <div
                  key={tag}
                  className="badge badge-accent"
                  style={{ padding: '6px 14px', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 8 }}
                >
                  <span>#{tag}</span>
                  <button
                    type="button"
                    onClick={() => setTags(tags.filter(item => item !== tag))}
                    style={{ border: 'none', background: 'none', color: 'var(--accent)', cursor: 'pointer', padding: 0, fontSize: 16, lineHeight: 1, fontWeight: 800 }}
                    aria-label={`${tag} 태그 제거`}
                  >
                    x
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 32, borderTop: '1px solid var(--border)', paddingTop: 24 }}>
          <button type="submit" disabled={loading} className="modern-btn modern-btn-primary">
            {isEdit ? '수정 완료' : '주제 등록'}
          </button>
          <button type="button" onClick={() => navigate(-1)} className="modern-btn modern-btn-secondary">
            취소
          </button>
        </div>
      </form>
    </div>
  );
};

export default TopicAreaFormPage;
